/**
 * Every route pattern a project's requests can be logged under. A CLOSED set.
 *
 * WHY THIS IS A PACKAGE AND NOT A FILE IN THE DATA PLANE
 *
 * Three things need this list and they cannot import each other: the project
 * Worker writes it, the dispatcher writes it for the two Workers that answer
 * outside the project Worker, and the control plane builds a SQL filter by
 * looking a route UP in it.
 *
 * It used to be two hand-maintained copies kept honest by a test asserting
 * they were equal. That test replaced an earlier one which compared a
 * hand-written list of paths against a hand-written allowlist and stayed
 * green while seven routes were added - and a route missing from the control
 * plane's copy was silently DROPPED from the query's WHERE clause, so asking
 * for one project's crawler logs returned its entire log.
 *
 * A third copy for the dispatcher would have been the same bug again. One
 * definition makes the disagreement impossible rather than merely detected,
 * which is the better kind of fix: the test that checked it can be deleted.
 *
 * CLOSED IS THE OTHER HALF OF THE POINT
 *
 * `routePattern` used to return any `/v1/...` path unchanged, so a scanner
 * walking `/v1/` wrote one distinct value per guess into the column
 * everything groups by. Nothing a caller typed reaches the log.
 */
export const ROUTE_PATTERNS = [
    '/',
    '/_nimblybase',
    '/:site',
    '/v1/sql',
    '/v1/sql/batch',
    '/v1/crawl',
    '/v1/ai/chat',
    '/v1/ai/models',
    '/v1/storage',
    '/v1/storage/sign',
    '/v1/storage/:key',
    '/v1/realtime',
    '/v1/realtime/ticket',
    '/v1/realtime/user-ticket',
    '/v1/realtime/publish',
    '/v1/realtime/disconnect',
    '/v1/schema',
    '/v1/schema/plan',
    '/v1/schema/apply',
    '/v1/migrations',
    '/v1/vectors/query',
    '/v1/vectors/status',
    '/v1/vectors/upsert',
    '/v1/vectors/embed',
    '/v1/vectors/delete',
    '/v1/rows/:table',
    '/v1/policies',
    '/v1/policies/:id',
    '/v1/site',
    '/v1/site/plan',
    '/v1/site/commit',
    '/v1/site/releases',
    '/v1/site/blob/:hash',
    '/v1/site/rollback/:id',
    /**
     * The two the DISPATCHER logs, because the Workers that answer them do
     * not log at all - a customer's own function and the auth service.
     *
     * `/fn/:name` and not `/fn/<name>`: the name is the customer's, and one
     * route per function would scatter their names through a column meant
     * for structure. The function's own name goes on the usage EVENT as an
     * attribute, where cost-per-function needs it.
     */
    '/fn/:name',
    '/auth',
    /** Anything under /v1 that is not a route. Always a 404; never a name. */
    '/v1/:unknown',
];
const PATTERNS = new Set(ROUTE_PATTERNS);
/** Whether a route can be filtered on. Used to refuse at an API edge. */
export function isKnownRoute(route) {
    return PATTERNS.has(route);
}
/**
 * The route PATTERN for a path.
 *
 * A storage key, a table name or a function name in the route column would
 * make grouping useless - every request would be its own route - and would
 * scatter customer-chosen names through a column meant for structure. The
 * key is still available where it belongs, in the message of a failure.
 *
 * The result is always a member of ROUTE_PATTERNS.
 */
export function routePattern(path) {
    const shaped = shape(path);
    if (PATTERNS.has(shaped))
        return shaped;
    // A path under /v1 that is not a route we serve. It 404s, and the name in
    // it is the caller's invention - one value, not one per invention.
    return path === '/v1' || path.startsWith('/v1/') ? '/v1/:unknown' : '/:site';
}
function shape(path) {
    // The dispatcher's two, checked first: `/fn/` and `/auth` never reach the
    // project Worker, and nothing else claims those prefixes.
    if (path === '/fn' || path.startsWith('/fn/'))
        return '/fn/:name';
    if (path === '/auth' || path.startsWith('/auth/'))
        return '/auth';
    if (path === '/v1/storage' || path === '/v1/storage/sign')
        return path;
    if (path.startsWith('/v1/storage/'))
        return '/v1/storage/:key';
    // A table name is the customer's; the route column groups by shape.
    if (path === '/v1/rows' || path.startsWith('/v1/rows/'))
        return '/v1/rows/:table';
    if (path.startsWith('/v1/policies/'))
        return '/v1/policies/:id';
    if (path.startsWith('/v1/site/blob/'))
        return '/v1/site/blob/:hash';
    if (path.startsWith('/v1/site/rollback/'))
        return '/v1/site/rollback/:id';
    if (path === '/v1' || path.startsWith('/v1/'))
        return path;
    // Everything else is a request to the customer's static site, and the path
    // is whatever a visitor or a crawler typed. Left alone it would put an
    // unbounded number of distinct values into the column - one per URL anybody
    // has ever tried. The site is one route.
    return path === '/' || path === '/_nimblybase' ? path : '/:site';
}
//# sourceMappingURL=routes.js.map